import { CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TaxAlert } from "@/features/tax";

import {
  Banner,
  solidGhostButtonClass,
  solidPrimaryButtonClass,
} from "../../../../../components/banner";

interface PanelActionBannerProps {
  isTaxCompliant: boolean;
  isTaxFormLoading: boolean;
  isActionPending: boolean;
  onAccept: () => void;
  onReject: () => void;
}

export const PanelActionBanner = ({
  isTaxCompliant,
  isTaxFormLoading,
  isActionPending,
  onAccept,
  onReject,
}: PanelActionBannerProps) => {
  const { t } = useTranslation();

  if (isTaxFormLoading) {
    return (
      <div
        role="status"
        aria-label={t("loading_text")}
        className="flex items-center justify-between gap-3 rounded-2xl bg-blue-600 p-4"
      >
        <Skeleton className="h-5 w-32 bg-white/30" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-20 rounded-md bg-white/30" />
          <Skeleton className="h-10 w-20 rounded-md bg-white/30" />
        </div>
      </div>
    );
  }

  if (!isTaxCompliant) {
    return <TaxAlert isTaxCompliant={isTaxCompliant} hasPendingStipends />;
  }

  return (
    <Banner
      tone="attention"
      icon={<CircleAlert aria-hidden="true" />}
      title={t("payments_pleaseAcceptOrReject")}
      actions={
        <>
          <Button
            type="button"
            onClick={onReject}
            disabled={isActionPending}
            className={solidGhostButtonClass}
            data-test-id="reject-button"
          >
            {t("reject")}
          </Button>
          <Button
            type="button"
            onClick={onAccept}
            loading={isActionPending}
            className={solidPrimaryButtonClass}
            data-test-id="accept-button"
          >
            {t("accept")}
          </Button>
        </>
      }
    />
  );
};
